import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { db } from '../firebase';
import Loading from './Loading';
import cisSeed from '../data/cisSeed2026_27.json';
import '../styles/CISTracker.css';

const TAX_YEAR = '2026/27';
const RATE_OPTIONS = [
  { value: 0, label: '0%' },
  { value: 0.2, label: '20%' },
  { value: 0.3, label: '30%' },
];

const money = (value) =>
  `£${Number(value || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

// Same logic as Excel:
// Liable = Gross - Materials
// CIS Deduction = Liable × Rate
// Net to sub = Gross - CIS Deduction
const calcDeduction = ({ grossPayment, materialsCost, cisRate }) => {
  const gross = round2(grossPayment);
  const materials = round2(materialsCost);
  const rate = Number(cisRate) || 0;
  const amountLiable = round2(gross - materials);
  const cisDeduction = round2(amountLiable * rate);
  const netPayment = round2(gross - cisDeduction);
  return { grossPayment: gross, materialsCost: materials, amountLiable, cisRate: rate, cisDeduction, netPayment };
};

// Suffered logic from Excel:
// CIS Suffered = Gross × Rate
// Net Received = Gross - CIS Suffered
const calcSuffered = ({ grossValue, cisRate }) => {
  const gross = round2(grossValue);
  const rate = Number(cisRate) || 0;
  const cisSuffered = round2(gross * rate);
  const netReceived = round2(gross - cisSuffered);
  return { grossValue: gross, cisRate: rate, cisSuffered, netReceived };
};

const emptyDeductionForm = {
  invoiceDate: '',
  cisPeriod: '',
  subcontractorName: '',
  invoiceNumber: '',
  grossPayment: '',
  materialsCost: '0',
  cisRate: '0.2',
};

const emptySufferedForm = {
  invoiceDate: '',
  cisPeriod: '',
  contractorName: '',
  invoiceNumber: '',
  grossValue: '',
  cisRate: '0.2',
  paymentDate: '',
  certificateReceived: false,
  notes: '',
};

function CISTracker() {
  const [tab, setTab] = useState('deducted');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deductions, setDeductions] = useState([]);
  const [suffered, setSuffered] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [deductionForm, setDeductionForm] = useState(emptyDeductionForm);
  const [sufferedForm, setSufferedForm] = useState(emptySufferedForm);
  const [editingDeductionId, setEditingDeductionId] = useState(null);
  const [editingSufferedId, setEditingSufferedId] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dedSnap, sufSnap, subSnap] = await Promise.all([
        getDocs(collection(db, 'cisDeductions')),
        getDocs(collection(db, 'cisSuffered')),
        getDocs(collection(db, 'subcontractors')),
      ]);

      const dedList = [];
      dedSnap.forEach((d) => dedList.push({ id: d.id, ...d.data() }));
      dedList.sort((a, b) => String(b.invoiceDate || '').localeCompare(String(a.invoiceDate || '')));

      const sufList = [];
      sufSnap.forEach((d) => sufList.push({ id: d.id, ...d.data() }));
      sufList.sort((a, b) => String(b.invoiceDate || '').localeCompare(String(a.invoiceDate || '')));

      const subList = [];
      subSnap.forEach((d) => subList.push({ id: d.id, ...d.data() }));
      subList.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));

      setDeductions(dedList);
      setSuffered(sufList);
      setSubcontractors(subList);
    } catch (error) {
      console.error('Error loading CIS data:', error);
      alert('Failed to load CIS data. Check Firestore permissions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const totals = useMemo(() => {
    const yearDeductions = deductions.filter((d) => (d.taxYear || TAX_YEAR) === TAX_YEAR);
    const yearSuffered = suffered.filter((s) => (s.taxYear || TAX_YEAR) === TAX_YEAR);
    const totalDeducted = round2(yearDeductions.reduce((sum, d) => sum + Number(d.cisDeduction || 0), 0));
    const totalSuffered = round2(yearSuffered.reduce((sum, s) => sum + Number(s.cisSuffered || 0), 0));
    // Same as Excel summary: Net CIS Position = Suffered - Deducted
    const netPosition = round2(totalSuffered - totalDeducted);
    return { totalDeducted, totalSuffered, netPosition };
  }, [deductions, suffered]);

  const deductionPreview = useMemo(
    () =>
      calcDeduction({
        grossPayment: deductionForm.grossPayment,
        materialsCost: deductionForm.materialsCost,
        cisRate: deductionForm.cisRate,
      }),
    [deductionForm]
  );

  const sufferedPreview = useMemo(
    () =>
      calcSuffered({
        grossValue: sufferedForm.grossValue,
        cisRate: sufferedForm.cisRate,
      }),
    [sufferedForm]
  );

  const importSeedData = async () => {
    if (deductions.length > 0 || suffered.length > 0) {
      const ok = window.confirm(
        'CIS records already exist. Import anyway? This will add the Excel rows again and may create duplicates.'
      );
      if (!ok) return;
    } else {
      const ok = window.confirm('Import your CIS tracking 2026/27 Excel data into Flair Portal?');
      if (!ok) return;
    }

    try {
      setSaving(true);
      for (const row of cisSeed.deductions) {
        await addDoc(collection(db, 'cisDeductions'), {
          ...row,
          createdAt: new Date().toISOString(),
          source: 'excel-import',
        });
      }
      for (const row of cisSeed.suffered) {
        await addDoc(collection(db, 'cisSuffered'), {
          ...row,
          createdAt: new Date().toISOString(),
          source: 'excel-import',
        });
      }
      await loadData();
      alert('Excel CIS data imported.');
    } catch (error) {
      console.error('Import failed:', error);
      alert('Failed to import CIS data.');
    } finally {
      setSaving(false);
    }
  };

  const resetDeductionForm = () => {
    setDeductionForm(emptyDeductionForm);
    setEditingDeductionId(null);
  };

  const resetSufferedForm = () => {
    setSufferedForm(emptySufferedForm);
    setEditingSufferedId(null);
  };

  const saveDeduction = async (e) => {
    e.preventDefault();
    if (!deductionForm.subcontractorName.trim() || !deductionForm.grossPayment) {
      alert('Subcontractor and gross payment are required');
      return;
    }

    const calculated = calcDeduction({
      grossPayment: deductionForm.grossPayment,
      materialsCost: deductionForm.materialsCost,
      cisRate: deductionForm.cisRate,
    });

    const payload = {
      invoiceDate: deductionForm.invoiceDate || '',
      cisPeriod: deductionForm.cisPeriod || '',
      subcontractorName: deductionForm.subcontractorName.trim(),
      invoiceNumber: deductionForm.invoiceNumber.trim(),
      ...calculated,
      taxYear: TAX_YEAR,
      updatedAt: new Date().toISOString(),
    };

    try {
      setSaving(true);
      if (editingDeductionId) {
        await updateDoc(doc(db, 'cisDeductions', editingDeductionId), payload);
      } else {
        await addDoc(collection(db, 'cisDeductions'), {
          ...payload,
          createdAt: new Date().toISOString(),
        });
      }
      resetDeductionForm();
      await loadData();
    } catch (error) {
      console.error('Error saving deduction:', error);
      alert('Failed to save CIS deduction');
    } finally {
      setSaving(false);
    }
  };

  const saveSuffered = async (e) => {
    e.preventDefault();
    if (!sufferedForm.contractorName.trim() || !sufferedForm.grossValue) {
      alert('Contractor and gross value are required');
      return;
    }

    const calculated = calcSuffered({
      grossValue: sufferedForm.grossValue,
      cisRate: sufferedForm.cisRate,
    });

    const payload = {
      invoiceDate: sufferedForm.invoiceDate || '',
      cisPeriod: sufferedForm.cisPeriod || '',
      contractorName: sufferedForm.contractorName.trim(),
      invoiceNumber: sufferedForm.invoiceNumber.trim(),
      ...calculated,
      paymentDate: sufferedForm.paymentDate || '',
      certificateReceived: Boolean(sufferedForm.certificateReceived),
      notes: sufferedForm.notes.trim(),
      taxYear: TAX_YEAR,
      updatedAt: new Date().toISOString(),
    };

    try {
      setSaving(true);
      if (editingSufferedId) {
        await updateDoc(doc(db, 'cisSuffered', editingSufferedId), payload);
      } else {
        await addDoc(collection(db, 'cisSuffered'), {
          ...payload,
          createdAt: new Date().toISOString(),
        });
      }
      resetSufferedForm();
      await loadData();
    } catch (error) {
      console.error('Error saving suffered entry:', error);
      alert('Failed to save CIS suffered entry');
    } finally {
      setSaving(false);
    }
  };

  const editDeduction = (row) => {
    setEditingDeductionId(row.id);
    setDeductionForm({
      invoiceDate: row.invoiceDate || '',
      cisPeriod: row.cisPeriod || '',
      subcontractorName: row.subcontractorName || '',
      invoiceNumber: row.invoiceNumber || '',
      grossPayment: String(row.grossPayment ?? ''),
      materialsCost: String(row.materialsCost ?? '0'),
      cisRate: String(row.cisRate ?? '0.2'),
    });
    setTab('deducted');
  };

  const editSufferedRow = (row) => {
    setEditingSufferedId(row.id);
    setSufferedForm({
      invoiceDate: row.invoiceDate || '',
      cisPeriod: row.cisPeriod || '',
      contractorName: row.contractorName || '',
      invoiceNumber: row.invoiceNumber || '',
      grossValue: String(row.grossValue ?? ''),
      cisRate: String(row.cisRate ?? '0.2'),
      paymentDate: row.paymentDate || '',
      certificateReceived: Boolean(row.certificateReceived),
      notes: row.notes || '',
    });
    setTab('suffered');
  };

  const removeDeduction = async (id) => {
    if (!window.confirm('Delete this CIS deduction record?')) return;
    try {
      await deleteDoc(doc(db, 'cisDeductions', id));
      await loadData();
    } catch (error) {
      console.error(error);
      alert('Failed to delete record');
    }
  };

  const removeSuffered = async (id) => {
    if (!window.confirm('Delete this CIS suffered record?')) return;
    try {
      await deleteDoc(doc(db, 'cisSuffered', id));
      await loadData();
    } catch (error) {
      console.error(error);
      alert('Failed to delete record');
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="cis-page">
      <div className="cis-header">
        <div className="cis-header-left">
          <Link to="/dashboard" className="home-button">← Home</Link>
          <h1>CIS Tracker</h1>
          <span className="cis-year-badge">Tax year {TAX_YEAR}</span>
        </div>
        <button
          type="button"
          className="cis-import-button"
          onClick={importSeedData}
          disabled={saving}
        >
          Import Excel 2026/27 data
        </button>
      </div>

      <div className="cis-summary">
        <div className="cis-summary-card suffered">
          <div className="cis-summary-label">CIS Suffered</div>
          <div className="cis-summary-value">{money(totals.totalSuffered)}</div>
        </div>
        <div className="cis-summary-card deducted">
          <div className="cis-summary-label">CIS Deducted</div>
          <div className="cis-summary-value">{money(totals.totalDeducted)}</div>
        </div>
        <div className="cis-summary-card net">
          <div className="cis-summary-label">Net CIS Position</div>
          <div className="cis-summary-value">{money(totals.netPosition)}</div>
          <div className="cis-summary-note">Suffered − Deducted (same as Excel)</div>
        </div>
      </div>

      <div className="cis-tabs">
        <button
          type="button"
          className={tab === 'deducted' ? 'active' : ''}
          onClick={() => setTab('deducted')}
        >
          CIS Deducted
        </button>
        <button
          type="button"
          className={tab === 'suffered' ? 'active' : ''}
          onClick={() => setTab('suffered')}
        >
          CIS Suffered
        </button>
      </div>

      {tab === 'deducted' && (
        <div className="cis-panel">
          <form className="cis-form" onSubmit={saveDeduction}>
            <h2>{editingDeductionId ? 'Edit deduction' : 'Add CIS deduction'}</h2>
            <div className="cis-form-grid">
              <label>
                Invoice Date
                <input
                  type="date"
                  value={deductionForm.invoiceDate}
                  onChange={(e) => setDeductionForm({ ...deductionForm, invoiceDate: e.target.value })}
                />
              </label>
              <label>
                CIS Period
                <input
                  type="date"
                  value={deductionForm.cisPeriod}
                  onChange={(e) => setDeductionForm({ ...deductionForm, cisPeriod: e.target.value })}
                />
              </label>
              <label>
                Subcontractor Name
                <input
                  list="cis-sub-list"
                  value={deductionForm.subcontractorName}
                  onChange={(e) => setDeductionForm({ ...deductionForm, subcontractorName: e.target.value })}
                  required
                />
                <datalist id="cis-sub-list">
                  {subcontractors.map((s) => (
                    <option key={s.id} value={s.name} />
                  ))}
                </datalist>
              </label>
              <label>
                Invoice Number
                <input
                  value={deductionForm.invoiceNumber}
                  onChange={(e) => setDeductionForm({ ...deductionForm, invoiceNumber: e.target.value })}
                />
              </label>
              <label>
                Gross Payment (Labour + Materials) (£)
                <input
                  type="number"
                  step="0.01"
                  value={deductionForm.grossPayment}
                  onChange={(e) => setDeductionForm({ ...deductionForm, grossPayment: e.target.value })}
                  required
                />
              </label>
              <label>
                Materials Cost (£)
                <input
                  type="number"
                  step="0.01"
                  value={deductionForm.materialsCost}
                  onChange={(e) => setDeductionForm({ ...deductionForm, materialsCost: e.target.value })}
                />
              </label>
              <label>
                CIS Deduction Rate
                <select
                  value={deductionForm.cisRate}
                  onChange={(e) => setDeductionForm({ ...deductionForm, cisRate: e.target.value })}
                >
                  {RATE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="cis-calc-row">
              <div>Amount Liable: <strong>{money(deductionPreview.amountLiable)}</strong></div>
              <div>CIS Deduction: <strong>{money(deductionPreview.cisDeduction)}</strong></div>
              <div>Net Payment: <strong>{money(deductionPreview.netPayment)}</strong></div>
            </div>

            <div className="cis-form-actions">
              {editingDeductionId && (
                <button type="button" className="secondary-button" onClick={resetDeductionForm}>
                  Cancel edit
                </button>
              )}
              <button type="submit" className="primary-button" disabled={saving}>
                {saving ? 'Saving…' : editingDeductionId ? 'Update record' : 'Add record'}
              </button>
            </div>
          </form>

          <div className="cis-table-wrap">
            <table className="cis-table">
              <thead>
                <tr>
                  <th>Invoice Date</th>
                  <th>Period</th>
                  <th>Subcontractor</th>
                  <th>Invoice #</th>
                  <th>Gross</th>
                  <th>Materials</th>
                  <th>Liable</th>
                  <th>Rate</th>
                  <th>CIS</th>
                  <th>Net</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {deductions.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="cis-empty">No deduction records yet. Import Excel data or add one above.</td>
                  </tr>
                ) : (
                  deductions.map((row) => (
                    <tr key={row.id}>
                      <td>{row.invoiceDate || '—'}</td>
                      <td>{row.cisPeriod || '—'}</td>
                      <td>{row.subcontractorName}</td>
                      <td>{row.invoiceNumber || '—'}</td>
                      <td>{money(row.grossPayment)}</td>
                      <td>{money(row.materialsCost)}</td>
                      <td>{money(row.amountLiable)}</td>
                      <td>{Math.round(Number(row.cisRate || 0) * 100)}%</td>
                      <td>{money(row.cisDeduction)}</td>
                      <td>{money(row.netPayment)}</td>
                      <td className="cis-row-actions">
                        <button type="button" onClick={() => editDeduction(row)}>Edit</button>
                        <button type="button" className="danger" onClick={() => removeDeduction(row.id)}>Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'suffered' && (
        <div className="cis-panel">
          <form className="cis-form" onSubmit={saveSuffered}>
            <h2>{editingSufferedId ? 'Edit suffered entry' : 'Add CIS suffered'}</h2>
            <div className="cis-form-grid">
              <label>
                Date
                <input
                  type="date"
                  value={sufferedForm.invoiceDate}
                  onChange={(e) => setSufferedForm({ ...sufferedForm, invoiceDate: e.target.value })}
                />
              </label>
              <label>
                CIS Period
                <input
                  type="date"
                  value={sufferedForm.cisPeriod}
                  onChange={(e) => setSufferedForm({ ...sufferedForm, cisPeriod: e.target.value })}
                />
              </label>
              <label>
                Contractor Name
                <input
                  value={sufferedForm.contractorName}
                  onChange={(e) => setSufferedForm({ ...sufferedForm, contractorName: e.target.value })}
                  required
                />
              </label>
              <label>
                Invoice Number
                <input
                  value={sufferedForm.invoiceNumber}
                  onChange={(e) => setSufferedForm({ ...sufferedForm, invoiceNumber: e.target.value })}
                />
              </label>
              <label>
                Gross Value (£)
                <input
                  type="number"
                  step="0.01"
                  value={sufferedForm.grossValue}
                  onChange={(e) => setSufferedForm({ ...sufferedForm, grossValue: e.target.value })}
                  required
                />
              </label>
              <label>
                CIS Rate
                <select
                  value={sufferedForm.cisRate}
                  onChange={(e) => setSufferedForm({ ...sufferedForm, cisRate: e.target.value })}
                >
                  {RATE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Payment Date
                <input
                  type="date"
                  value={sufferedForm.paymentDate}
                  onChange={(e) => setSufferedForm({ ...sufferedForm, paymentDate: e.target.value })}
                />
              </label>
              <label className="cis-checkbox">
                <input
                  type="checkbox"
                  checked={sufferedForm.certificateReceived}
                  onChange={(e) => setSufferedForm({ ...sufferedForm, certificateReceived: e.target.checked })}
                />
                CIS Certificate Received
              </label>
              <label className="full-width">
                Notes
                <input
                  value={sufferedForm.notes}
                  onChange={(e) => setSufferedForm({ ...sufferedForm, notes: e.target.value })}
                />
              </label>
            </div>

            <div className="cis-calc-row">
              <div>CIS Suffered: <strong>{money(sufferedPreview.cisSuffered)}</strong></div>
              <div>Net Received: <strong>{money(sufferedPreview.netReceived)}</strong></div>
            </div>

            <div className="cis-form-actions">
              {editingSufferedId && (
                <button type="button" className="secondary-button" onClick={resetSufferedForm}>
                  Cancel edit
                </button>
              )}
              <button type="submit" className="primary-button" disabled={saving}>
                {saving ? 'Saving…' : editingSufferedId ? 'Update record' : 'Add record'}
              </button>
            </div>
          </form>

          <div className="cis-table-wrap">
            <table className="cis-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Period</th>
                  <th>Contractor</th>
                  <th>Invoice #</th>
                  <th>Gross</th>
                  <th>Rate</th>
                  <th>CIS Suffered</th>
                  <th>Net Received</th>
                  <th>Paid</th>
                  <th>Cert</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {suffered.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="cis-empty">No suffered records yet. Import Excel data or add one above.</td>
                  </tr>
                ) : (
                  suffered.map((row) => (
                    <tr key={row.id}>
                      <td>{row.invoiceDate || '—'}</td>
                      <td>{row.cisPeriod || '—'}</td>
                      <td>{row.contractorName}</td>
                      <td>{row.invoiceNumber || '—'}</td>
                      <td>{money(row.grossValue)}</td>
                      <td>{Math.round(Number(row.cisRate || 0) * 100)}%</td>
                      <td>{money(row.cisSuffered)}</td>
                      <td>{money(row.netReceived)}</td>
                      <td>{row.paymentDate || '—'}</td>
                      <td>{row.certificateReceived ? 'Yes' : 'No'}</td>
                      <td className="cis-row-actions">
                        <button type="button" onClick={() => editSufferedRow(row)}>Edit</button>
                        <button type="button" className="danger" onClick={() => removeSuffered(row.id)}>Delete</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default CISTracker;
